/**
 * Модалка «Поля карточки» позиции справочника экологии (ecology-card-fields-admin): какие поля видит
 * клиент в карточке строки раздела. Файл заказчика 23.09.2026 — построчная ревизия карточки по
 * каждой позиции («оставить / удалить / переименовать / добавить поле»); следующие порции таких
 * правок администратор вносит здесь, без релиза.
 *
 * Встроенные поля: подпись можно заменить у любого, скрыть — «Действует до» и «Номер, кем выдан»
 * (даты разработки и сдачи не скрываются — по ним считается статус). Скрытое поле клиент не видит,
 * в статусе и письмах о сроках оно не участвует, но внесённые значения остаются в строках.
 *
 * Дополнительные поля: подпись, подсказка, тип, обязательность и порядок. Тип существующего поля не
 * меняется — на нём держатся значения клиентов; удалённое поле уносит значения при сохранении.
 * Настройка сохраняется целиком одним запросом.
 */
import { useEffect, useId, useState, type FormEvent } from 'react'
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react'
import {
  adminApi,
  type EcoBuiltinField,
  type EcoCardField,
  type EcoCatalogItem,
  type EcoHideableField,
} from '@/lib/api'
import { ecoFieldIsFile } from '@/lib/ecology'
import { getErrorMessage } from '@/lib/errors'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

/** Встроенные поля карточки по виду позиции: общая подпись и можно ли скрыть. */
const BUILTIN_FIELDS: Record<
  EcoCatalogItem['kind'],
  { field: EcoBuiltinField; label: string; hideable: boolean }[]
> = {
  document: [
    { field: 'developed_on', label: 'Дата разработки', hideable: false },
    { field: 'valid_until', label: 'Действует до', hideable: true },
    { field: 'doc_number', label: 'Номер, кем выдан', hideable: true },
    { field: 'note', label: 'Примечание', hideable: false },
  ],
  report: [
    { field: 'last_submitted_on', label: 'Дата последней сдачи', hideable: false },
    { field: 'note', label: 'Примечание', hideable: false },
  ],
}

const FIELD_TYPE_LABELS: Record<EcoCardField['type'], string> = {
  number: 'Целое число',
  site_nvos: 'Код объекта и категория площадки',
  file: 'Файл (один)',
  file_multi: 'Файлы (несколько)',
  fkko_list: 'Отходы по ФККО (код и наименование)',
}

/** Больше не принимает сервер (`domain.NormalizeEcoCardConfig`). */
const MAX_FIELDS = 10

/** Черновик дополнительного поля; `uid` — ключ строки формы, `id` — у сохранённого поля. */
interface FieldDraft {
  uid: string
  id?: string
  label: string
  hint: string
  type: EcoCardField['type']
  required: boolean
  group: string
}

let draftSeq = 0
const nextUid = () => `new-${++draftSeq}`

function draftsFrom(item: EcoCatalogItem): FieldDraft[] {
  return item.fields.map((f) => ({
    uid: f.id,
    id: f.id,
    label: f.label,
    hint: f.hint,
    type: f.type,
    required: f.required,
    group: f.group,
  }))
}

export function CardFieldsDialog({
  open,
  item,
  onClose,
  onSaved,
}: {
  open: boolean
  item: EcoCatalogItem | null
  onClose: () => void
  onSaved: (item: EcoCatalogItem) => void
}) {
  const formId = useId()
  const [hidden, setHidden] = useState<EcoHideableField[]>([])
  const [labels, setLabels] = useState<Partial<Record<EcoBuiltinField, string>>>({})
  const [fields, setFields] = useState<FieldDraft[]>([])
  const [showErrors, setShowErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !item) return
    setHidden(item.hiddenFields)
    setLabels(item.fieldLabels)
    setFields(draftsFrom(item))
    setShowErrors(false)
    setError(null)
  }, [open, item])

  if (!item) return null
  const builtin = BUILTIN_FIELDS[item.kind]
  const removed = item.fields.filter((f) => !fields.some((d) => d.id === f.id))

  const fieldErrors: Record<string, string> = {}
  for (const f of fields) if (!f.label.trim()) fieldErrors[f.uid] = 'Укажите подпись поля'
  const tooMany = fields.length > MAX_FIELDS
  const siteFields = fields.filter((f) => f.type === 'site_nvos').length
  const hasErrors = Object.keys(fieldErrors).length > 0 || tooMany || siteFields > 1

  const patchField = (uid: string, next: Partial<FieldDraft>) =>
    setFields((prev) => prev.map((f) => (f.uid === uid ? { ...f, ...next } : f)))
  const moveField = (index: number, delta: number) =>
    setFields((prev) => {
      const next = [...prev]
      const [f] = next.splice(index, 1)
      next.splice(index + delta, 0, f)
      return next
    })

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving || !item) return
    if (hasErrors) {
      setShowErrors(true)
      return
    }
    setSaving(true)
    setError(null)
    try {
      const saved = await adminApi.ecology.saveCard(item.id, {
        hiddenFields: hidden,
        fieldLabels: labels,
        fields: fields.map((f) => ({
          ...(f.id ? { id: f.id } : {}),
          label: f.label.trim(),
          hint: f.hint.trim(),
          type: f.type,
          required: f.type === 'number' && f.required,
          group: f.group.trim(),
        })),
      })
      onSaved(saved)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить поля карточки'))
    } finally {
      setSaving(false)
    }
  }

  const title = `Поля карточки — ${item.name}`
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) onClose()
      }}
    >
      <DialogContent title={title} className="max-w-2xl">
        <Card
          scrollable
          className="max-h-dialog"
          header={title}
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Отмена
              </Button>
              <Button type="submit" form={formId} disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
          }
        >
          <form id={formId} onSubmit={submit} noValidate>
            <p className="text-13 text-txt-secondary">
              Какие поля клиент видит в карточке строки этой позиции. Изменения видны клиентам
              сразу.
            </p>
            {error ? (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            ) : null}

            <h3 className="mb-1 mt-5 text-14 font-semibold text-txt">Встроенные поля</h3>
            <p className="mb-2 text-12 text-txt-tertiary">
              Скрытое поле клиент не видит, в статусе и письмах о сроках оно не участвует; внесённые
              раньше значения остаются в строках.
            </p>
            <ul className="flex flex-col gap-3">
              {builtin.map((b) => {
                const isHidden = b.hideable && hidden.includes(b.field as EcoHideableField)
                return (
                  <li key={b.field} className="grid grid-cols-1 items-center gap-2 nw:grid-cols-3">
                    {b.hideable ? (
                      <LabeledCheckbox
                        checked={!isHidden}
                        onCheckedChange={(on) =>
                          setHidden((prev) =>
                            on
                              ? prev.filter((f) => f !== b.field)
                              : [...prev, b.field as EcoHideableField],
                          )
                        }
                      >
                        {b.label}
                      </LabeledCheckbox>
                    ) : (
                      <span className="text-14 text-txt">{b.label}</span>
                    )}
                    <Input
                      className="nw:col-span-2"
                      aria-label={`Подпись поля «${b.label}»`}
                      value={labels[b.field] ?? ''}
                      placeholder={isHidden ? 'Поле скрыто' : `${b.label} (общая подпись)`}
                      disabled={isHidden}
                      onChange={(e) =>
                        setLabels((prev) => ({ ...prev, [b.field]: e.target.value }))
                      }
                    />
                  </li>
                )
              })}
            </ul>

            <h3 className="mb-1 mt-6 text-14 font-semibold text-txt">Дополнительные поля</h3>
            <p className="mb-2 text-12 text-txt-tertiary">
              Показываются в карточке после встроенных, в этом порядке. «Код объекта и категория»
              хранятся в площадке — правка в карточке документа меняет площадку. Файлы клиент
              прикрепляет в карточке уже внесённой строки; они попадают и в «Документы».
            </p>
            {fields.length === 0 ? (
              <p className="text-13 text-txt-secondary">Дополнительных полей нет.</p>
            ) : (
              <ol className="flex flex-col gap-3">
                {fields.map((f, i) => (
                  <li key={f.uid} className="rounded-10 border border-line p-3">
                    <div className="flex items-start gap-2">
                      <span className="mt-2 text-13 text-txt-tertiary">{i + 1}.</span>
                      <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-3 nw:grid-cols-2">
                        <Field
                          label="Подпись"
                          required
                          error={showErrors ? fieldErrors[f.uid] : undefined}
                          className="my-1"
                        >
                          {(props) => (
                            <Input
                              {...props}
                              aria-label={`Подпись поля ${i + 1}`}
                              value={f.label}
                              invalid={showErrors && Boolean(fieldErrors[f.uid])}
                              onChange={(e) => patchField(f.uid, { label: e.target.value })}
                            />
                          )}
                        </Field>
                        <Field label="Тип" className="my-1">
                          {(props) => (
                            <Select
                              value={f.type}
                              disabled={Boolean(f.id)}
                              onValueChange={(v) =>
                                patchField(f.uid, { type: v as EcoCardField['type'] })
                              }
                            >
                              <SelectTrigger
                                id={props.id}
                                aria-label={`Тип поля ${i + 1}`}
                                className="text-left"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {(Object.keys(FIELD_TYPE_LABELS) as EcoCardField['type'][]).map(
                                  (t) => (
                                    <SelectItem key={t} value={t}>
                                      {FIELD_TYPE_LABELS[t]}
                                    </SelectItem>
                                  ),
                                )}
                              </SelectContent>
                            </Select>
                          )}
                        </Field>
                        <Field
                          label="Подсказка клиенту"
                          hint="Необязательно"
                          className="my-1 nw:col-span-2"
                        >
                          {(props) => (
                            <Input
                              {...props}
                              aria-label={`Подсказка поля ${i + 1}`}
                              value={f.hint}
                              onChange={(e) => patchField(f.uid, { hint: e.target.value })}
                            />
                          )}
                        </Field>
                        <Field
                          label="Группа в карточке"
                          hint="Подзаголовок, под которым поле стоит в карточке, — например, «Проект СЗЗ». Необязательно"
                          className="my-1 nw:col-span-2"
                        >
                          {(props) => (
                            <Input
                              {...props}
                              aria-label={`Группа поля ${i + 1}`}
                              maxLength={100}
                              value={f.group}
                              onChange={(e) => patchField(f.uid, { group: e.target.value })}
                            />
                          )}
                        </Field>
                        {f.type === 'number' ? (
                          <div className="nw:col-span-2">
                            <LabeledCheckbox
                              checked={f.required}
                              onCheckedChange={(on) => patchField(f.uid, { required: on })}
                            >
                              Обязательное
                            </LabeledCheckbox>
                            <p className="mt-1 text-12 text-txt-tertiary">
                              Карточку строки без значения не сохранить. Строки из опроса, заказа и
                              внесённые сразу на несколько площадок создаются без него.
                            </p>
                          </div>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 flex-col gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          aria-label={`Поднять поле ${i + 1}`}
                          disabled={i === 0}
                          onClick={() => moveField(i, -1)}
                        >
                          <ArrowUp className="size-4 stroke-current" aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          aria-label={`Опустить поле ${i + 1}`}
                          disabled={i === fields.length - 1}
                          onClick={() => moveField(i, 1)}
                        >
                          <ArrowDown className="size-4 stroke-current" aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          aria-label={`Удалить поле ${i + 1}`}
                          onClick={() => setFields((prev) => prev.filter((d) => d.uid !== f.uid))}
                        >
                          <X className="size-4 stroke-current" aria-hidden />
                        </Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
            {showErrors && tooMany ? (
              <p role="alert" className="mt-2 text-12 text-red">
                Не больше {MAX_FIELDS} дополнительных полей
              </p>
            ) : null}
            {showErrors && siteFields > 1 ? (
              <p role="alert" className="mt-2 text-12 text-red">
                Поле «Код объекта и категория» может быть только одно
              </p>
            ) : null}
            <div className="mt-3">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={fields.length >= MAX_FIELDS}
                leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
                onClick={() =>
                  setFields((prev) => [
                    ...prev,
                    {
                      uid: nextUid(),
                      label: '',
                      hint: '',
                      type: 'number',
                      required: false,
                      group: '',
                    },
                  ])
                }
              >
                Добавить поле
              </Button>
            </div>
            {removed.length > 0 ? (
              <Alert variant="danger" className="mt-4">
                При сохранении удалятся поля {removed.map((f) => `«${f.label}»`).join(', ')} — и
                значения, которые клиенты в них внесли.
                {removed.some((f) => ecoFieldIsFile(f.type))
                  ? ' Прикреплённые файлы останутся у клиентов в «Документах».'
                  : null}
              </Alert>
            ) : null}
          </form>
        </Card>
      </DialogContent>
    </Dialog>
  )
}
